/**
 * Active Initiatives Widget (issue #1818, successor to #1656, part of epic
 * #1650)
 *
 * Active `ChangeInitiative`s (default: `proposed`/`approved`/`in-progress`),
 * each with its `IMPACTS`-edge-derived impacted-Service count, matching
 * `homepage-dashboard-spec.md`'s "Active Change Initiatives" widget. Calls
 * the `activeInitiatives` GraphQL query directly (ADR-0003 GraphQL-for-reads
 * — no REST route) built by the prerequisite issue #1746
 * (`backend/src/graphql/resolvers/changeInitiatives.ts`). Follows the
 * single-query + `useStatisticsWidget` pattern shared by
 * `DependencyHealthWidget`.
 *
 * Per issue #1761 (open, tracked separately, not a blocker here):
 * production `ChangeInitiative` rows have no `status` set yet, so this
 * widget renders its empty state against today's data — expected until
 * #1761's backfill lands.
 */

import { useActiveInitiatives, type ActiveInitiativeEntry } from '../../graphql/queries';
import { formatDate } from '../../lib/dateFormat';
import { DashboardWidget } from './DashboardWidget';
import { useStatisticsWidget } from './useStatisticsWidget';
import type { WidgetConfig } from './types';
import '@tylertech/forge/icon';
import './ActiveInitiativesWidget.css';

const WIDGET_CONFIG: WidgetConfig = {
  id: 'active-initiatives',
  title: 'Active Initiatives',
  size: 'medium',
  refreshInterval: 600000, // 10 minutes — matches the sibling widgets' cadence
  showRefresh: true,
  showExpand: false,
};

interface ActiveInitiativesStats {
  initiatives: ActiveInitiativeEntry[];
}

type ActiveInitiativesQueryData = ReturnType<typeof useActiveInitiatives>['data'];

function extractActiveInitiativesStats(
  data: ActiveInitiativesQueryData
): ActiveInitiativesStats | null {
  if (!data?.activeInitiatives) {
    return null;
  }
  return data.activeInitiatives;
}

/** Formats an ISO date range for display; either end may be absent. */
function formatDateRange(startDate: string | null, endDate: string | null): string {
  if (!startDate && !endDate) return 'No dates set';
  const start = startDate ? formatDate(startDate) : '?';
  const end = endDate ? formatDate(endDate) : '?';
  return `${start} – ${end}`;
}

export function ActiveInitiativesWidget(): JSX.Element {
  const { stats, widgetState, handleRefresh } = useStatisticsWidget(
    useActiveInitiatives(),
    extractActiveInitiativesStats,
    WIDGET_CONFIG.refreshInterval!
  );

  const IconElement = 'forge-icon' as React.ElementType;

  if (!stats || stats.initiatives.length === 0) {
    return (
      <DashboardWidget config={WIDGET_CONFIG} state={widgetState} onRefresh={handleRefresh}>
        <div className="active-initiatives-widget__empty">
          <IconElement name="rocket_launch" className="active-initiatives-widget__empty-icon" />
          <p className="forge-typography--body2">No active change initiatives</p>
        </div>
      </DashboardWidget>
    );
  }

  return (
    <DashboardWidget config={WIDGET_CONFIG} state={widgetState} onRefresh={handleRefresh}>
      <ul className="active-initiatives-widget__list" data-testid="active-initiatives-list">
        {stats.initiatives.map((initiative) => (
          <li key={initiative.id} className="active-initiatives-widget__item">
            <div className="active-initiatives-widget__item-header">
              <span className="active-initiatives-widget__item-name forge-typography--body2">
                {initiative.name}
              </span>
              {initiative.status && (
                <span
                  className={`active-initiatives-widget__status active-initiatives-widget__status--${initiative.status}`}
                >
                  {initiative.status}
                </span>
              )}
            </div>
            <div className="active-initiatives-widget__item-meta forge-typography--caption">
              {formatDateRange(initiative.startDate, initiative.endDate)} ·{' '}
              {initiative.impactedServicesCount} service
              {initiative.impactedServicesCount === 1 ? '' : 's'} impacted
            </div>
          </li>
        ))}
      </ul>
    </DashboardWidget>
  );
}
